import {
  assistantAudienceProposalDataSchema,
  type AssistantAudienceProposalData,
} from '@charitystack/dashboard-assistant-contracts';
import {
  removeStorageValue,
  replaceStorageValue,
  type NativeAudiencePersistenceIdentity,
} from './nativeAudiencePersistence';

export type NativeAudienceProposalHandoff = {
  storageKey: string;
  requestId: string;
  writerId: string;
};

export type StoredNativeAudienceProposalHandoff = {
  version: 1;
  writerId: string;
  owner: {
    organizationId: string;
    actorId: string;
    sessionId: string;
    assistantSessionId: string;
    tabId: string;
  };
  requestId: string;
  data: AssistantAudienceProposalData;
};

type StorageReaderWriter = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function stableProposalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableProposalJson).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableProposalJson(item)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

export async function proposalHash(proposal: unknown, sessionId: string) {
  const bytes = new TextEncoder().encode(stableProposalJson({ proposal, sessionId }));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

export async function validateAudienceProposalBinding({
  value,
  organizationId,
  actorId,
  sessionId,
  now = Date.now(),
}: {
  value: unknown;
  organizationId: string;
  actorId: string;
  sessionId: string;
  now?: number;
}): Promise<AssistantAudienceProposalData> {
  const data = assistantAudienceProposalDataSchema.parse(value);
  if (data.proposal.organizationId !== organizationId) {
    throw new Error('This segment proposal belongs to another organization.');
  }
  if (data.proposal.actorId !== actorId) {
    throw new Error('This segment proposal was prepared for another user.');
  }
  if (data.sessionId !== sessionId) {
    throw new Error('This segment proposal belongs to another chat session.');
  }
  if (Date.parse(data.proposal.expiresAt) <= now) {
    throw new Error('This segment proposal expired. Ask the assistant to prepare a fresh proposal.');
  }
  const calculated = await proposalHash(data.proposal, data.sessionId);
  if (calculated !== data.proposalHash) {
    throw new Error('This segment proposal changed after validation. Ask for a fresh proposal.');
  }
  return data;
}

function keyPart(value: string) {
  return encodeURIComponent(value);
}

export function proposalStorageKey(
  organizationId: string,
  identity: NativeAudiencePersistenceIdentity,
  proposalId: string,
) {
  return `native-audience-proposal-handoff:v1:${[
    organizationId,
    identity.actorId,
    identity.sessionId,
    identity.tabId,
    identity.assistantSessionId ?? 'no-assistant-session',
    proposalId,
  ].map(keyPart).join(':')}`;
}

export function createStoredAudienceProposalHandoff({
  data,
  identity,
  organizationId,
  writerId,
}: {
  data: AssistantAudienceProposalData;
  identity: NativeAudiencePersistenceIdentity;
  organizationId: string;
  writerId: string;
}): StoredNativeAudienceProposalHandoff {
  if (!identity.assistantSessionId) throw new Error('Open this proposal in its original signed-in chat session.');
  return {
    version: 1,
    writerId,
    owner: {
      organizationId,
      actorId: identity.actorId,
      sessionId: identity.sessionId,
      assistantSessionId: identity.assistantSessionId,
      tabId: identity.tabId,
    },
    requestId: data.proposal.proposalId,
    data,
  };
}

export function decodeStoredAudienceProposalHandoff(
  value: string | null,
): StoredNativeAudienceProposalHandoff | null {
  if (!value) return null;
  let parsed: Partial<StoredNativeAudienceProposalHandoff>;
  try {
    parsed = JSON.parse(value) as Partial<StoredNativeAudienceProposalHandoff>;
  } catch {
    return null;
  }
  const owner = parsed.owner as Partial<StoredNativeAudienceProposalHandoff['owner']> | undefined;
  const data = assistantAudienceProposalDataSchema.safeParse(parsed.data);
  if (
    parsed.version !== 1 ||
    typeof parsed.writerId !== 'string' ||
    typeof parsed.requestId !== 'string' ||
    typeof owner?.organizationId !== 'string' ||
    typeof owner.actorId !== 'string' ||
    typeof owner.sessionId !== 'string' ||
    typeof owner.assistantSessionId !== 'string' ||
    typeof owner.tabId !== 'string' ||
    !data.success
  ) return null;
  return { ...(parsed as StoredNativeAudienceProposalHandoff), data: data.data };
}

export function restoreStoredAudienceProposalHandoff(
  value: string | null,
  identity: NativeAudiencePersistenceIdentity,
  organizationId: string,
  requestId: string,
) {
  const stored = decodeStoredAudienceProposalHandoff(value);
  return stored &&
    identity.assistantSessionId &&
    stored.owner.organizationId === organizationId &&
    stored.owner.actorId === identity.actorId &&
    stored.owner.sessionId === identity.sessionId &&
    stored.owner.assistantSessionId === identity.assistantSessionId &&
    stored.owner.tabId === identity.tabId &&
    stored.requestId === requestId
    ? stored
    : null;
}

export function storeAudienceProposalHandoff(
  storage: StorageReaderWriter,
  key: string,
  handoff: StoredNativeAudienceProposalHandoff,
) {
  const nextValue = JSON.stringify(handoff);
  let currentValue: string | null;
  try {
    currentValue = storage.getItem(key);
  } catch {
    return null;
  }
  if (currentValue === nextValue) return nextValue;
  if (currentValue !== null) {
    const current = decodeStoredAudienceProposalHandoff(currentValue);
    if (
      current &&
      current.writerId === handoff.writerId &&
      current.requestId === handoff.requestId &&
      stableProposalJson(current.owner) === stableProposalJson(handoff.owner) &&
      stableProposalJson(current.data) === stableProposalJson(handoff.data)
    ) return currentValue;
    return null;
  }
  return replaceStorageValue(storage, key, null, nextValue) ? nextValue : null;
}

export function removeAudienceProposalHandoff(
  storage: StorageReaderWriter,
  key: string,
  expectedValue: string,
  identity: NativeAudiencePersistenceIdentity,
  organizationId: string,
  requestId: string,
  writerId: string,
) {
  const stored = restoreStoredAudienceProposalHandoff(
    expectedValue,
    identity,
    organizationId,
    requestId,
  );
  return Boolean(
    stored?.writerId === writerId &&
    removeStorageValue(storage, key, expectedValue),
  );
}
